import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// eslint-disable-next-line import/no-unresolved
import type { QualityGap, Resolution } from "@astrana/ui/modules";

import {
  fetchQualityGapClosures,
  setQualityGapClosure,
  type QualityGapClosure,
} from "./qualityGapClosuresApi";

export interface UseQualityGapClosuresArgs {
  /**
   * astranaId of the member being viewed, or `undefined` to disable the
   * hook entirely (feature flag off, or no astranaId resolvable for this
   * member — e.g. a PP-search-opened (`pp:`) member, which carries neither a
   * Pathways record nor an astranaId anywhere in this host today). Passing
   * `undefined` skips the fetch and makes `onToggleGap` a no-op, so the
   * checklist simply stays disabled rather than risk writing a closure
   * against the wrong member (PHI).
   */
  astranaId: string | undefined;
  /**
   * MUST match the card's own default (astrana-ui-modules'
   * `useQualityGaps` hook defaults to `new Date().getFullYear()`) or
   * hydration silently mismatches and closures appear to vanish.
   */
  measureYear: number;
  /** Raw Provider Portal API base URL (`VITE_PROVIDER_PORTAL_API_BASE_URL`,
   *  no trailing "/api" — this hook appends it), or `undefined` when PP-API
   *  isn't configured in this environment. */
  ppApiBaseUrl: string | undefined;
  /**
   * The signed-in identity's current, identity-verified PP bearer
   * (`auth/ppToken.ts`'s `getPpToken`), or `undefined` if there isn't one
   * active right now (never linked, or a linked session's access token has
   * lapsed since the last render).
   */
  ppToken: string | undefined;
  /**
   * Mints a fresh PP bearer — silently, off the persisted refresh token,
   * when possible, else opens the EXISTING PpLoginModal gate
   * (MemberProfilePage's shared `ensurePpSession`/`PpLoginGate` — see
   * `auth/ppLoginGate.ts`, `auth/PpLoginModal.tsx`). REUSED, never
   * duplicated: this hook has no login/gate mechanism of its own. Rejects
   * with `PpLoginCancelledError` if the user cancels the prompt.
   */
  ensurePpSession: () => Promise<void>;
}

export interface UseQualityGapClosuresResult {
  /** submeasureCodes currently closed (per @astrana/ui's `getGapKey`). */
  closedGapIds: string[];
  /**
   * AM-6518 — host-owned per-gap Mark-status resolution, keyed by
   * `submeasureCode` (== `getGapKey` for every gap this hook can key at all —
   * see `qualityGapClosuresApi.ts`'s doc). Wired straight to the QualityGaps
   * card's `gapResolutions` controlled prop: a key ABSENT from this map falls
   * back to the card's own internal state, so an unhydrated gap doesn't
   * flash as "unset" before the GET lands — see the hydration effect below,
   * which seeds this from every returned row (including `resolution: null`
   * rows, which DO map to an explicit `null` here — "hydrated and
   * confirmed-unset" is a real state, not "still loading").
   */
  gapResolutions: Record<string, Resolution | null>;
  /**
   * Wired straight to the QualityGaps card's `onSetResolution`. Persists the
   * resolution AND its equivalent `closed` value in a single POST (server
   * rule: `"Pending"` ⇒ `closed:false`, every other resolution ⇒
   * `closed:true`; `null` clears back to `closed:false`/`resolution:null`).
   *
   * The card fires this immediately before `onToggleGap` for the SAME pick
   * (upstream contract) — that follow-up boolean call is a no-op here
   * because this write already covers it; see `onToggleGap`'s dedupe check
   * below for the mechanism.
   */
  onSetResolution: (gapKey: string, resolution: Resolution | null, gap: QualityGap) => void;
  /**
   * Wired straight to the QualityGaps card's `onToggleGap`.
   *
   * When there is no LIVE PP bearer right now (never linked, or a linked
   * session's token has lapsed) the click only calls `ensurePpSession` —
   * silently refreshing, or prompting via the existing PpLoginModal — and
   * does NOT write in the same gesture; the user must click again once
   * linked. This mirrors the existing "Link Provider Portal account"
   * affordance elsewhere on this page (one click links, a second acts) and
   * guarantees the POST is never attempted unauthenticated.
   *
   * Also a no-op — never even reaching `ensurePpSession` — whenever
   * `readOnly` is true (see below): a gap list we couldn't load is not safe
   * to accept edits against.
   */
  onToggleGap: (gapKey: string) => void;
  loading: boolean;
  /** Last error from EITHER the hydration GET or a toggle POST. Informational
   *  only — the shipped QualityGaps component (v0.4.59) doesn't render
   *  `props.error`, so this exists for host-side surfacing (toasts, logging),
   *  not for driving the card's own UI. See `readOnly` for the signal that
   *  DOES drive the card. */
  error: Error | null;
  /**
   * True ONLY when the initial closures GET failed (providerPortalAPI
   * unreachable, endpoint not yet deployed, migration not applied — e.g. a
   * bare 500 from `42P01 undefined_table`). Deliberately NOT true for a
   * successful GET that simply returned zero closures — an empty result is a
   * legitimate, fully-interactive state; only a failed READ downgrades the
   * card. Wired to the QualityGaps card's `readOnly` prop: rendering
   * unchecked, POST-able toggles against a gap list we couldn't actually load
   * would let a user "save" a change that silently never persists.
   */
  readOnly: boolean;
}

/**
 * ACT-1342 — AstraConnect's own read/write path for user-asserted Quality
 * Gaps checklist closures, now backed by providerPortalAPI (REST) instead of
 * the abandoned care-management-gql GraphQL path. The QualityGaps card is
 * controlled and owns no store/fetch/mutation; this hook is the host half of
 * that contract.
 */
export function useQualityGapClosures(
  args: UseQualityGapClosuresArgs,
): UseQualityGapClosuresResult {
  const { astranaId, measureYear, ppApiBaseUrl, ppToken, ensurePpSession } = args;

  const [closures, setClosures] = useState<QualityGapClosure[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Dedicated to the HYDRATION (initial GET) outcome only — deliberately
  // separate from `error` above, which also carries toggle/POST failures.
  // A failed POST should not retroactively make an already-loaded, correct
  // gap list read-only; only a failed READ should.
  const [hydrationFailed, setHydrationFailed] = useState(false);

  // Live mirror of `ppToken`, read inside `onToggleGap`'s async continuation
  // AFTER an `await ensurePpSession()`. `ensurePpSession` (MemberProfilePage)
  // uses `flushSync` to force a fresh render — and therefore a fresh
  // `ppToken` prop — to commit BEFORE its promise resolves, but the already
  // -running async closure below only closed over whatever `ppToken` was
  // current when `onToggleGap` was CALLED. Reading through a ref updated on
  // every render is the same live-value pattern MemberProfilePage itself
  // uses for this exact hazard (`ppSessionCtxRef`/`embedCtxRef`).
  const ppTokenRef = useRef(ppToken);
  ppTokenRef.current = ppToken;

  // Monotonic guard against a slow initial GET stomping a faster toggle POST
  // (and against a now-superseded GET stomping a newer one). Every GET
  // attempt captures the CURRENT counter value when it STARTS; a toggle POST
  // bumps the counter right before it applies its own result. Whichever one
  // is the LAST to bump/capture "wins" — if a GET's result lands after the
  // counter has moved on (a POST committed meanwhile, or a newer GET
  // superseded it), that GET's result is discarded instead of overwriting
  // fresher state. A plain `cancelled` boolean (below) only guards the
  // dependency-change/unmount case — it does NOT see an interleaved POST
  // committed from a DIFFERENT effect instance, which is exactly the gap
  // that let a toggle silently revert.
  const versionRef = useRef(0);

  // AM-6518 dedupe: the QualityGaps card fires `onSetResolution` and then,
  // ONLY WHEN the pick flips the gap's checked state (desiredChecked !==
  // currently checked), `onToggleGap` SYNCHRONOUSLY right after, for the
  // same user pick — the resolution write already POSTs the equivalent
  // `closed` value, so that paired boolean call must not also fire a second,
  // redundant POST.
  //
  // This is deliberately NOT keyed on the closed value the two calls would
  // each compute (a prior version was, and leaked): a terminal-to-terminal
  // pick (e.g. Completed -> Scheduled/Referred) or a Pending/clear pick on a
  // gap that's already open never flips checked state at all, so the card
  // never calls `onToggleGap` for it — a value-keyed entry recorded by
  // `onSetResolution` would then never be consumed, and would either swallow
  // the user's NEXT, unrelated manual toggle of that same gap, or (if values
  // happened to mismatch) fire a stray POST with `resolution` omitted.
  //
  // Instead: time-boxed and identity-only. `onSetResolution` records only
  // WHICH gap it just wrote and an expiry a little past "the same tick"
  // (the card's follow-up call, when it happens, is synchronous — same
  // microtask, well under 100ms); `onToggleGap` checks solely "is this the
  // same gap, and are we still inside that window" — never the value. A
  // match is consumed (cleared) unconditionally so it can never persist past
  // its one intended use; if the pick didn't flip checked state, the entry
  // just naturally expires and is a no-op for the next real event.
  const suppressToggleRef = useRef<{ gapKey: string; until: number } | null>(null);
  // How long a resolution write suppresses a same-gap `onToggleGap` call.
  // Only needs to bridge "the rest of this synchronous event-handler tick",
  // not any real async gap — kept short so it can never mask a genuine
  // later manual toggle of the same gap.
  const TOGGLE_SUPPRESSION_WINDOW_MS = 100;

  useEffect(() => {
    if (!astranaId || !ppApiBaseUrl || !ppToken) {
      setClosures([]);
      setError(null);
      setHydrationFailed(false);
      return;
    }
    let cancelled = false;
    const version = ++versionRef.current;
    setLoading(true);
    setError(null);
    // Reset optimistically at the START of every fresh attempt (not just on
    // success below) — a retry (dependency change, e.g. a just-completed PP
    // link handing this effect a fresh `ppToken`) deserves a clean slate
    // rather than staying stuck read-only from a previous failure.
    setHydrationFailed(false);
    fetchQualityGapClosures(ppApiBaseUrl, ppToken, astranaId, measureYear)
      .then((result) => {
        // Superseded by a toggle POST (or a newer GET) that committed while
        // this one was in flight — never overwrite fresher state with a
        // stale read.
        if (cancelled || versionRef.current !== version) return;
        // A successful GET — even one returning zero closures — is a fully
        // legitimate, interactive state. Only a FAILED read (below) sets
        // hydrationFailed.
        setClosures(result);
      })
      .catch((err: unknown) => {
        if (cancelled || versionRef.current !== version) return;
        setError(
          err instanceof Error ? err : new Error("Failed to load quality gap closures."),
        );
        // The read failed — we genuinely don't know this member's closure
        // state, so offering interactive toggles (which would silently fail
        // to persist against the same unreachable/undeployed endpoint) is
        // unsafe. Degrade to read-only rather than rendering every gap as
        // falsely "open and toggleable".
        setHydrationFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [astranaId, measureYear, ppApiBaseUrl, ppToken]);

  const closedGapIds = useMemo(
    () => closures.filter((c) => c.closed).map((c) => c.submeasureCode),
    [closures],
  );

  // AM-6518 — seeded straight off the same GET rows as `closedGapIds` above;
  // `resolution` is cast from the API's bare `string | null` to `Resolution
  // | null` on the trust that providerPortalAPI only ever stores one of the
  // five values it itself validates on write (see `SetQualityGapClosureInput`
  // 's doc) — this hook never invents or normalizes a resolution value.
  const gapResolutions = useMemo(
    () =>
      Object.fromEntries(
        closures.map((c) => [c.submeasureCode, c.resolution as Resolution | null]),
      ),
    [closures],
  );

  const onSetResolution = useCallback(
    (gapKey: string, resolution: Resolution | null, _gap: QualityGap) => {
      // Same guard as `onToggleGap` below — never write against unknown
      // closure state.
      if (!astranaId || !ppApiBaseUrl || hydrationFailed) return;

      const nextClosed = resolution !== null && resolution !== "Pending";

      // Record BEFORE the `await` below so the very next synchronous call —
      // the card's paired `onToggleGap` for this same pick, IF it fires at
      // all (see this ref's declaration) — sees it.
      suppressToggleRef.current = {
        gapKey,
        until: performance.now() + TOGGLE_SUPPRESSION_WINDOW_MS,
      };

      void (async () => {
        if (!ppTokenRef.current) {
          try {
            await ensurePpSession();
          } catch {
            // Cancelled — stay read-only, no write attempted.
          }
          return;
        }

        try {
          const updated = await setQualityGapClosure(ppApiBaseUrl, ppTokenRef.current, {
            astranaId,
            measureYear,
            submeasureCode: gapKey,
            closed: nextClosed,
            resolution,
          });
          versionRef.current += 1;
          setClosures((prev) => [
            ...prev.filter((c) => c.submeasureCode !== updated.submeasureCode),
            updated,
          ]);
          setError(null);
        } catch (err) {
          setError(
            err instanceof Error
              ? err
              : new Error("Failed to save the quality gap resolution."),
          );
        }
      })();
    },
    [astranaId, ppApiBaseUrl, measureYear, ensurePpSession, hydrationFailed],
  );

  const onToggleGap = useCallback(
    (gapKey: string) => {
      // `hydrationFailed` (below): the initial GET failed, so we don't
      // actually know this member's closure state. Never attempt a write
      // against unknown state — it would look like it "saved" while the
      // same unreachable/undeployed endpoint silently drops it.
      if (!astranaId || !ppApiBaseUrl || hydrationFailed) return;

      const nextClosed = !closedGapIds.includes(gapKey);

      // AM-6518 dedupe: identity-only, time-boxed — see `suppressToggleRef`'s
      // declaration for why this is NOT value-keyed. A match is consumed
      // (cleared) unconditionally, whether or not it's still inside the
      // window, so it can never linger and swallow a later, unrelated
      // manual toggle of the same gap.
      const suppress = suppressToggleRef.current;
      if (suppress?.gapKey === gapKey) {
        suppressToggleRef.current = null;
        if (performance.now() < suppress.until) return;
        // Window elapsed (the card never called us for this pick, or called
        // us unexpectedly late) — fall through and let the boolean POST
        // proceed rather than silently dropping a real toggle.
      }

      void (async () => {
        if (!ppTokenRef.current) {
          // No live bearer right now — request one (silent refresh, else the
          // existing PpLoginModal). This click links/refreshes only; it
          // never writes in the same gesture (see this fn's doc comment).
          try {
            await ensurePpSession();
          } catch {
            // Cancelled — stay read-only, no write attempted.
          }
          return;
        }

        try {
          const updated = await setQualityGapClosure(ppApiBaseUrl, ppTokenRef.current, {
            astranaId,
            measureYear,
            submeasureCode: gapKey,
            closed: nextClosed,
          });
          // Bump the shared version BEFORE applying — any GET already in
          // flight (which captured an older version at its own start) will
          // see the mismatch when it resolves and discard its now-stale
          // result instead of reverting this write.
          versionRef.current += 1;
          setClosures((prev) => [
            ...prev.filter((c) => c.submeasureCode !== updated.submeasureCode),
            updated,
          ]);
          setError(null);
        } catch (err) {
          // Swallow into `error` state rather than throwing out of an event
          // handler — the closure list is left at its last-known-good state
          // (no optimistic write applied), so a failed POST never leaves the
          // checkbox showing an unpersisted "closing" state.
          setError(
            err instanceof Error ? err : new Error("Failed to save the quality gap closure."),
          );
        }
      })();
    },
    [astranaId, ppApiBaseUrl, measureYear, closedGapIds, ensurePpSession, hydrationFailed],
  );

  return {
    closedGapIds,
    gapResolutions,
    onToggleGap,
    onSetResolution,
    loading,
    error,
    readOnly: hydrationFailed,
  };
}
